/**
 * characterData.ts
 * Static character definitions for the three playable characters.
 *
 * Each entry provides the sprite sheet layout, animation clips, and draw
 * offset needed by the renderer. Physics hitbox is identical across all
 * characters (NOVA_WIDTH × NOVA_HEIGHT from gameConstants).
 *
 * @module characterData
 */

import type { NovaAnimState } from '../engine/gameState';
import type { SpriteSheet, AnimClip } from '../rendering/SpriteRenderer';
import type { DrawOffset } from '../rendering/drawOffsets';
import type { CharacterId, CharacterMeta } from '../types/character';
import { NOVA_WIDTH, NOVA_HEIGHT } from '../../constants/gameConstants';

// ── Nova sprite data (re-uses existing SpriteRenderer constants) ──────────

// DECISION: Import NOVA_SHEET, NOVA_CLIPS, NOVA_DRAW_OFFSET from SpriteRenderer
// so there is one source of truth. Avon/Belle get their own constants here because
// their frame layouts differ significantly from Nova's.
import { NOVA_SHEET, NOVA_CLIPS, NOVA_DRAW_OFFSET } from '../rendering/SpriteRenderer';

// ── Avon sprite sheet ─────────────────────────────────────────────────────

// DECISION: Avon PixelLab output is 92×92 per frame (larger canvas). Packed and
// downscaled to 60×60 to match Nova/Belle for visual consistency. See
// scripts/packAvonSheet.js (which both packs from temp/avon/ and downscales).
// The previous two-step pipeline (pack at 92, then repackAvonSheet.js to 60)
// was collapsed into the single packer in PR #18.
// 52 frames in a single horizontal strip.

/** Avon sprite sheet: 3120 × 60 px, 52 frames at 60 × 60 px. */
export const AVON_SHEET: SpriteSheet = {
  sheetWidth: 3120,
  sheetHeight: 60,
  frameWidth: 60,
  frameHeight: 60,
  frameCount: 52,
};

/**
 * Avon animation clips — mapped to NovaAnimState for engine compatibility.
 * Frame indices match the packAvonSheet.js strip layout:
 *   IDLE[0-3], RUN[4-11], JUMP[12-15], CROUCH[16-20], STOMP[21-24],
 *   ATTACK[25-30], HURT[31-33], DEAD[34-39], FALL[40-43], LAND[44-47],
 *   RUN_STOP[48-51]
 */
export const AVON_CLIPS: Record<NovaAnimState, AnimClip> = {
  // ── Movement ──
  IDLE:        { start:  0, end:  3, fps: 3,  loop: true  },
  RUN_START:   { start:  4, end:  5, fps: 15, loop: false },
  RUN:         { start:  4, end: 11, fps: 12, loop: true  },
  RUN_STOP:    { start: 48, end: 51, fps: 12, loop: false },
  // DECISION: Frames 16–20 are a crouch-down animation (heights 46→33).
  // Frame 16 is still standing — frame 20 is the fully crouched pose.
  CROUCH:      { start: 20, end: 20, fps: 1,  loop: true  },
  JUMP_CROUCH: { start: 20, end: 20, fps: 15, loop: false },
  JUMP:        { start: 12, end: 15, fps: 10, loop: false },
  FALL:        { start: 40, end: 43, fps: 8,  loop: true  },
  LAND:        { start: 44, end: 47, fps: 12, loop: false },
  // ── Combat ──
  STOMP:       { start: 21, end: 24, fps: 14, loop: false },
  HURT:        { start: 31, end: 33, fps: 12, loop: false },
  DEAD:        { start: 34, end: 39, fps: 4,  loop: false },
  // ── Aim / Fire (reuse attack frame for now) ──
  AIM_RIGHT:       { start: 25, end: 25, fps: 1, loop: true },
  AIM_UP_RIGHT:    { start: 25, end: 25, fps: 1, loop: true },
  AIM_UP:          { start: 25, end: 25, fps: 1, loop: true },
  AIM_UP_LEFT:     { start: 25, end: 25, fps: 1, loop: true },
  AIM_LEFT:        { start: 25, end: 25, fps: 1, loop: true },
  AIM_DOWN_LEFT:   { start: 25, end: 25, fps: 1, loop: true },
  AIM_DOWN:        { start: 25, end: 25, fps: 1, loop: true },
  AIM_DOWN_RIGHT:  { start: 25, end: 25, fps: 1, loop: true },
  JUMP_FIRE_RIGHT:     { start: 25, end: 25, fps: 1, loop: true },
  JUMP_FIRE_UP_RIGHT:  { start: 25, end: 25, fps: 1, loop: true },
};

// DECISION: Avon’s 60×60 frames have medianH=40 (vs Nova’s 28). To match
// Nova’s on-screen size (65 * 28/60 ≈ 30px visual), drawSize = 30 * 60/40 = 46.
const AVON_DRAW_SIZE = 46;
const AVON_FEET_RATIO = 50 / 60;
export const AVON_DRAW_OFFSET: DrawOffset = {
  x: -(AVON_DRAW_SIZE - NOVA_WIDTH) / 2,
  y: NOVA_HEIGHT - AVON_FEET_RATIO * AVON_DRAW_SIZE,
  w: AVON_DRAW_SIZE,
  h: AVON_DRAW_SIZE,
};

// ── Belle sprite sheet ────────────────────────────────────────────────────

// DECISION: Belle PixelLab output is 60×60 per frame (same as Nova — she's
// a shorter character). 52 frames in a single horizontal strip.
// Frame layout matches packBelleSheet.js.

/** Belle sprite sheet: 3120 × 60 px, 52 frames at 60 × 60 px. */
export const BELLE_SHEET: SpriteSheet = {
  sheetWidth: 3120,
  sheetHeight: 60,
  frameWidth: 60,
  frameHeight: 60,
  frameCount: 52,
};

/**
 * Belle animation clips — identical frame layout to Avon, same 52-frame strip.
 *   IDLE[0-3], RUN[4-11], JUMP[12-15], CROUCH[16-20], STOMP[21-24],
 *   ATTACK[25-30], HURT[31-33], DEAD[34-39], FALL[40-43], LAND[44-47],
 *   RUN_STOP[48-51]
 */
export const BELLE_CLIPS: Record<NovaAnimState, AnimClip> = {
  // ── Movement ──
  IDLE:        { start:  0, end:  3, fps: 3,  loop: true  },
  RUN_START:   { start:  4, end:  5, fps: 15, loop: false },
  RUN:         { start:  4, end: 11, fps: 12, loop: true  },
  RUN_STOP:    { start: 48, end: 51, fps: 12, loop: false },
  // DECISION: Frames 16–20 are a crouch-down animation (heights 44→31).
  // Frame 16 is still standing — frame 20 is the fully crouched pose.
  CROUCH:      { start: 20, end: 20, fps: 1,  loop: true  },
  JUMP_CROUCH: { start: 20, end: 20, fps: 15, loop: false },
  JUMP:        { start: 12, end: 15, fps: 10, loop: false },
  FALL:        { start: 40, end: 43, fps: 8,  loop: true  },
  LAND:        { start: 44, end: 47, fps: 12, loop: false },
  // ── Combat ──
  STOMP:       { start: 21, end: 24, fps: 14, loop: false },
  HURT:        { start: 31, end: 33, fps: 12, loop: false },
  DEAD:        { start: 34, end: 39, fps: 4,  loop: false },
  // ── Aim / Fire (reuse attack frame for now) ──
  AIM_RIGHT:       { start: 25, end: 25, fps: 1, loop: true },
  AIM_UP_RIGHT:    { start: 25, end: 25, fps: 1, loop: true },
  AIM_UP:          { start: 25, end: 25, fps: 1, loop: true },
  AIM_UP_LEFT:     { start: 25, end: 25, fps: 1, loop: true },
  AIM_LEFT:        { start: 25, end: 25, fps: 1, loop: true },
  AIM_DOWN_LEFT:   { start: 25, end: 25, fps: 1, loop: true },
  AIM_DOWN:        { start: 25, end: 25, fps: 1, loop: true },
  AIM_DOWN_RIGHT:  { start: 25, end: 25, fps: 1, loop: true },
  JUMP_FIRE_RIGHT:     { start: 25, end: 25, fps: 1, loop: true },
  JUMP_FIRE_UP_RIGHT:  { start: 25, end: 25, fps: 1, loop: true },
};

// DECISION: Belle’s 60×60 frames have medianH=37 (vs Nova’s 28). To match
// Nova’s on-screen size (65 * 28/60 ≈ 30px visual), drawSize = 30 * 60/37 ≈ 49.
const BELLE_DRAW_SIZE = 49;
const BELLE_FEET_RATIO = 50 / 60;
export const BELLE_DRAW_OFFSET: DrawOffset = {
  x: -(BELLE_DRAW_SIZE - NOVA_WIDTH) / 2,
  y: NOVA_HEIGHT - BELLE_FEET_RATIO * BELLE_DRAW_SIZE,
  w: BELLE_DRAW_SIZE,
  h: BELLE_DRAW_SIZE,
};

// ── Character registry ────────────────────────────────────────────────────

/** All playable characters indexed by id. */
export const CHARACTERS: Record<CharacterId, CharacterMeta> = {
  nova: {
    id: 'nova',
    name: 'Nova',
    description: 'The fastest courier in the fleet. Doesn\'t waste words.',
    sheet: NOVA_SHEET,
    clips: NOVA_CLIPS,
    drawOffset: NOVA_DRAW_OFFSET,
    accentColor: '#f5c842',
    previewMetrics: { contentHeight: 29, centerX: 29.5, bottom: 46 },
  },
  avon: {
    id: 'avon',
    name: 'Avon',
    description: 'Adventure-hardened explorer. Fights with arcane fireball magic.',
    sheet: AVON_SHEET,
    clips: AVON_CLIPS,
    drawOffset: AVON_DRAW_OFFSET,
    accentColor: '#e07040',
    previewMetrics: { contentHeight: 44, centerX: 27.5, bottom: 53 },
  },
  belle: {
    id: 'belle',
    name: 'Belle',
    description: 'Nine years old, zero chill. Fights with a magic wand and big energy.',
    sheet: BELLE_SHEET,
    clips: BELLE_CLIPS,
    drawOffset: BELLE_DRAW_OFFSET,
    accentColor: '#d478e0',
    previewMetrics: { contentHeight: 44, centerX: 28.5, bottom: 53 },
  },
};

/**
 * Retrieve character metadata by id, defaulting to Nova if invalid.
 *
 * @param id - The character identifier.
 * @returns The CharacterMeta for the requested character.
 */
export function getCharacter(id: CharacterId): CharacterMeta {
  return CHARACTERS[id] ?? CHARACTERS.nova;
}
